<script setup>
import DialogDrawer from '@js/components/dialogs/DialogDrawer.vue';
import ComboboxField from '@js/components/fields/ComboboxField.vue';
import InputField from '@js/components/fields/InputField.vue';
import ListboxField from '@js/components/fields/ListboxField.vue';
import TextareaField from '@js/components/fields/TextareaField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import { useRoute } from '@js/utils/route';
import { toastError } from '@js/utils/toast';
import { reactive, ref } from 'vue';
import { object, string } from 'yup';

const types = [
    {
        value: 'premium',
        label: 'Premium',
    },
    {
        value: 'standard',
        label: 'Standard',
    },
];

const props = defineProps({
    tenant: {
        type: Object,
        required: true,
    },
    websiteVariants: {
        type: Array,
        required: true,
    },
    callback: {
        type: Function,
        required: false,
    },
});

const { route } = useRoute();

const isOpen = ref(false);

const isSubmitting = ref(false);

const form = reactive({
    websiteVariant: null,
    urls: '',
    type: types[0],
    date: '',
});

const validationSchema = object({
    websiteVariant: object().required('Ce champ est obligatoire.'),
    urls: string().required('Ce champ est obligatoire.'),
    type: object()
        .shape({
            value: string().required('Ce champ est obligatoire.'),
            label: string().required('Ce champ est obligatoire.'),
        })
        .required('Ce champ est obligatoire.'),
    date: string().nullable(),
});

function submit() {
    isSubmitting.value = true;

    axios
        .post(
            route('front.projectManagement.seo.postImportApikomContents', {
                tenant: props.tenant.id,
            }),
            {
                websiteVariantId: form.websiteVariant?.id,
                urls: form.urls,
                type: form.type.value,
                date: form.date,
            },
        )
        .then(() => {
            isOpen.value = false;

            if (props.callback) {
                props.callback();
            }

            form.websiteVariant = null;
            form.urls = '';
            form.date = '';
        })
        .finally(() => {
            isSubmitting.value = false;
        })
        .catch((error) => {
            toastError(error);
        });
}
</script>

<template>
    <DialogDrawer v-model="isOpen">
        <template #trigger>
            <slot name="trigger" />
        </template>

        <template #title> Importer </template>

        <template #full-content>
            <FormRoot
                :validation-schema="validationSchema"
                :submit="submit"
                v-slot="{ errors }"
            >
                <FormColumn>
                    <FormContent>
                        <ComboboxField
                            v-model="form.websiteVariant"
                            :options="props.websiteVariants"
                            field-name="websiteVariant"
                            field-id="websiteVariant"
                            label="Site"
                            filtered-option-key="name"
                            required
                        >
                            <template #optionContent="{ option }">
                                {{ option.name }}
                            </template>
                        </ComboboxField>

                        <TextareaField
                            v-model="form.urls"
                            label="URLs"
                            placeholder="URLs"
                            field-name="urls"
                            field-id="urls"
                            legend="Une URL par ligne"
                            required
                        />

                        <ListboxField
                            v-model="form.type"
                            field-id="type"
                            field-name="type"
                            label="Type"
                            :options="types"
                            by="value"
                            required
                        >
                            <template #buttonContent>
                                {{ form.type.label }}
                            </template>

                            <template #optionContent="{ option }">
                                {{ option.label }}
                            </template>
                        </ListboxField>

                        <InputField
                            v-model="form.date"
                            label="Date"
                            placeholder="Date"
                            field-name="date"
                            field-id="date"
                            type="month"
                        />
                    </FormContent>
                </FormColumn>

                <FormFooter
                    :is-submitting="isSubmitting"
                    :errors="errors"
                    without-back-button
                />
            </FormRoot>
        </template>
    </DialogDrawer>
</template>
